import { api } from '@/lib/api/client';
import { dbCount, dbReplaceAll } from '@/lib/db/db';
import { STORE_NAMES, type StoreName } from '@/lib/db/schema';
import { CATALOG_ROUTES } from '@/lib/models';
import { createStore } from '@/lib/store';

/**
 * Filling the catalog cache.
 *
 * The song lists and their friends are megabytes of read-only JSON that change only
 * when an operator re-imports. Downloading them once into IndexedDB is what lets a
 * song table render a name for every row without a join on the server or a request per
 * row in the browser.
 *
 * Two things decide whether a store is re-downloaded, and both are needed:
 *
 *  - **It is empty.** A first visit, or a user who cleared site data.
 *  - **The server's `dbVersion` rose.** A populated store is never otherwise checked,
 *    which is the whole point — but it also means a re-import on the server is
 *    invisible until that number moves. `ARTEMIS_DB_VERSION` is the operator's way of
 *    saying "what you have is stale".
 */

export type CatalogState = 'idle' | 'loading' | 'ok' | 'error';

type CatalogStates = Record<StoreName, CatalogState>;

function initialStates(): CatalogStates {
  return Object.fromEntries(STORE_NAMES.map((name) => [name, 'idle'])) as CatalogStates;
}

export const catalogStore = createStore<CatalogStates>(initialStates());

/** Which API path fills which store, inverted from the contract's own table. */
const ROUTE_FOR_STORE = new Map<StoreName, string>(
  Object.entries(CATALOG_ROUTES).map(([path, store]) => [store as StoreName, path]),
);

const VERSION_KEY = 'artemis.catalogVersion';

function storedVersion(): number | null {
  try {
    const raw = localStorage.getItem(VERSION_KEY);
    return raw === null ? null : Number.parseInt(raw, 10);
  } catch {
    return null;
  }
}

function rememberVersion(version: number): void {
  try {
    localStorage.setItem(VERSION_KEY, String(version));
  } catch {
    // Storage denied. The catalogs still load; they just re-check every visit, which
    // is a slow first paint rather than a broken one.
  }
}

function setState(store: StoreName, state: CatalogState): void {
  catalogStore.set({ ...catalogStore.get(), [store]: state });
}

interface DbVersionReply {
  state?: string;
  version?: { major?: number };
}

/** The server's catalog generation, or null when it cannot be read. */
async function serverVersion(): Promise<number | null> {
  try {
    // Un-enveloped by contract: `state` and `version.major` sit at the top level.
    const reply = await api.get<DbVersionReply>('api/static/dbVersion');
    const major = reply?.version?.major;
    return typeof major === 'number' ? major : null;
  } catch {
    return null;
  }
}

let running: Promise<void> | null = null;

/**
 * Bring every catalog up to date.
 *
 * Safe to call repeatedly — concurrent calls share one run. Never rejects: a portal
 * whose catalogs failed to load still shows every page, with ids where names would be,
 * and that is far better than an error screen in front of a working site.
 */
export function preloadCatalogs(): Promise<void> {
  running ??= run().finally(() => {
    running = null;
  });
  return running;
}

async function run(): Promise<void> {
  const version = await serverVersion();
  const known = storedVersion();
  const stale = version !== null && known !== null && version !== known;

  // Sequential on purpose. These are the largest downloads the portal makes, and
  // sixteen at once on a phone competes with the page's own requests for the whole
  // first paint.
  let allOk = true;
  for (const store of STORE_NAMES) {
    const ok = await fill(store, stale);
    allOk &&= ok;
  }

  // Only record the new generation once every store actually took it. Recording it
  // after a partial failure would leave those stores stale until the next bump.
  if (version !== null && allOk) rememberVersion(version);
}

async function fill(store: StoreName, stale: boolean): Promise<boolean> {
  const path = ROUTE_FOR_STORE.get(store);
  if (!path) return true;

  try {
    if (!stale && (await dbCount(store)) > 0) {
      setState(store, 'ok');
      return true;
    }

    setState(store, 'loading');
    // Catalogs answer as a bare array; an envelope here would be a `.map` on an object.
    const rows = await api.get<unknown[]>(path.replace(/^\//, ''));
    if (!Array.isArray(rows)) throw new Error(`${path} did not answer with an array`);

    await dbReplaceAll(store, rows);
    setState(store, 'ok');
    return true;
  } catch {
    setState(store, 'error');
    return false;
  }
}

/** Whether every store a page needs is loaded, for pages that read several. */
export function catalogsReady(states: CatalogStates, stores: readonly StoreName[]): boolean {
  return stores.every((store) => states[store] === 'ok');
}
